import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import cronstrue from "cronstrue";
import api, { updateAgentCron } from "api/axios";
import PureDropDown from "components/pure-dropdown";
import ScheduleConfiguration from "components/schedule/schedule-configuration";
import TooltipHorizon from "components/tooltip";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useAgentInfo } from "../../../../../layouts/agent/agent-info/_context/AgentInfoContext";
import { AUTONOMOUS_AGENT_STATUS } from "constants/agents";
import { useParams } from "react-router-dom";
import { Agent } from "../../../../../types";

const Trigger = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { agentInfo, setAgentDetails } = useAgentInfo();
  const currentOrigin = window.location.origin;
  const urlEndpoint = agentInfo?.autonomous_path;

  const agentCron = useMemo(() => agentInfo?.cron ?? "", [agentInfo]);
  const normalizedAgentCron = agentCron?.trim() ?? "";
  const scheduling = agentInfo?.schedule_type;

  const [lastSavedCron, setLastSavedCron] = useState(normalizedAgentCron);
  const [triggerType, setTriggerType] = useState(
    scheduling === AUTONOMOUS_AGENT_STATUS.IDLE ? null : scheduling
  );
  const [lastSavedTriggerType, setLastSavedTriggerType] = useState(
    scheduling === AUTONOMOUS_AGENT_STATUS.IDLE ? null : scheduling
  );
  const [cronExpression, setCronExpression] =
    useState<string>(normalizedAgentCron);
  const [isSaving, setIsSaving] = useState(false);
  const [isEndpointCopied, setIsEndpointCopied] = useState(false);

  useEffect(() => {
    setLastSavedCron(normalizedAgentCron);
    setCronExpression(normalizedAgentCron);
  }, [normalizedAgentCron]);

  useEffect(() => {
    const newTriggerType =
      scheduling === AUTONOMOUS_AGENT_STATUS.IDLE ? null : scheduling;
    setTriggerType(newTriggerType);
    setLastSavedTriggerType(newTriggerType);
  }, [scheduling]);

  const handleSaveConfiguration = async () => {
    if (!agentInfo?.id) {
      toast.error(t("agents.trigger.errors.missingAgentInfo"));
      return;
    }

    const cronValue =
      triggerType === AUTONOMOUS_AGENT_STATUS.SCHEDULING
        ? cronExpression.trim()
        : "";

    setIsSaving(true);
    try {
      const response = await updateAgentCron(
        agentInfo.id,
        cronValue,
        triggerType
      );
      const agentDetail = await api.get<Agent>(`/agents/${id}`);
      setAgentDetails(agentDetail.data);
      const nextCron = (response?.cron ?? cronValue).trim();

      setLastSavedCron(nextCron);
      setCronExpression(nextCron);
      const savedType =
        nextCron.length > 0 ? AUTONOMOUS_AGENT_STATUS.SCHEDULING : triggerType;
      setTriggerType(savedType);
      setLastSavedTriggerType(savedType);
    } catch (error) {
      console.error("Failed to update agent cron:", error);
      toast.error(t("agents.trigger.errors.updateFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const triggerOptions = useMemo(
    () => [
      {
        value: AUTONOMOUS_AGENT_STATUS.SCHEDULING,
        label: t("agents.newAgent.triggerTypes.schedule"),
      },
      {
        value: AUTONOMOUS_AGENT_STATUS.API_TRIGGER,
        label: t("agents.newAgent.triggerTypes.API_Trigger"),
      },
    ],
    [t]
  );

  const cronDescription = useMemo(() => {
    if (triggerType !== AUTONOMOUS_AGENT_STATUS.SCHEDULING) {
      return { text: "", isValid: true };
    }

    const value = cronExpression.trim();

    if (!value) {
      return { text: "", isValid: true };
    }

    try {
      const description = cronstrue.toString(value, {
        use24HourTimeFormat: true,
      });

      return { text: description, isValid: true };
    } catch (error) {
      console.error("Failed to convert cron expression:", error);
      return {
        text: t("agents.trigger.cronDescriptionError"),
        isValid: false,
      };
    }
  }, [cronExpression, triggerType, t]);

  const normalizedCronExpression = cronExpression.trim();
  const normalizedLastSavedCron = lastSavedCron.trim();
  const savedTriggerType =
    normalizedLastSavedCron.length > 0
      ? AUTONOMOUS_AGENT_STATUS.SCHEDULING
      : null;
  const hasChanges =
    triggerType !== savedTriggerType ||
    (triggerType === AUTONOMOUS_AGENT_STATUS.SCHEDULING &&
      normalizedCronExpression !== normalizedLastSavedCron);
  const isCronValid =
    triggerType !== AUTONOMOUS_AGENT_STATUS.SCHEDULING ||
    normalizedCronExpression.length > 0;

  const isSaveButtonDisabled =
    triggerType === AUTONOMOUS_AGENT_STATUS.API_TRIGGER
      ? triggerType === lastSavedTriggerType || isSaving
      : !hasChanges || !isCronValid || isSaving;

  const handleTriggerTypeChange = (value: string | string[]) => {
    const selected = Array.isArray(value) ? value[0] : value;
    const triggerValue =
      !selected || selected === ""
        ? null
        : (selected as
            | AUTONOMOUS_AGENT_STATUS.SCHEDULING
            | AUTONOMOUS_AGENT_STATUS.API_TRIGGER);
    setTriggerType(triggerValue);
  };

  const handleResetChanges = () => {
    setTriggerType(lastSavedTriggerType);
    setCronExpression(normalizedLastSavedCron);
  };

  const handleCopyEndpoint = async () => {
    if (!urlEndpoint) {
      return;
    }

    const fullUrl = `${currentOrigin}${urlEndpoint}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setIsEndpointCopied(true);
      toast.success(t("agents.trigger.apiEndpointCopySuccess"));
      setTimeout(() => setIsEndpointCopied(false), 3000);
    } catch (error) {
      console.error("Failed to copy API endpoint:", error);
      toast.error(t("agents.trigger.apiEndpointCopyError"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t("agents.trigger.executionModeTitle")}
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {t("agents.trigger.triggerTypeDescription")}
          </p>
        </div>

        <PureDropDown
          label={t("agents.newAgent.triggerType")}
          options={triggerOptions}
          value={triggerType ?? undefined}
          onChange={handleTriggerTypeChange}
          placeholder={t("agents.newAgent.selectTriggerType")}
          onClear={() => setTriggerType(null)}
          className="w-full"
          buttonClassName="rounded-xl border border-gray-200 bg-white/0 px-3 py-3 text-sm text-gray-900 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-white/0 dark:text-white dark:hover:border-white/20"
        />

        {triggerType === AUTONOMOUS_AGENT_STATUS.SCHEDULING && (
          <>
            <ScheduleConfiguration
              value={cronExpression}
              onChange={setCronExpression}
              label={t("agents.trigger.autoTitle")}
              className="mt-4"
            />
            {cronDescription.text && (
              <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:bg-white/5 dark:text-gray-300">
                {cronDescription.isValid ? (
                  <p>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t("agents.trigger.cronDescriptionLabel")}:
                    </span>{" "}
                    {cronDescription.text}
                  </p>
                ) : (
                  <p className="text-red-500 dark:text-red-400">
                    {cronDescription.text}
                  </p>
                )}
              </div>
            )}
          </>
        )}
        {triggerType === AUTONOMOUS_AGENT_STATUS.API_TRIGGER && urlEndpoint && (
          <div className="mt-4 flex items-end gap-3">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700 dark:text-white">
                {t("agents.trigger.apiEndpointLabel")}
              </label>
              <div className="mt-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 dark:border-navy-600 dark:bg-navy-700">
                <span className="font-mono text-sm text-gray-600 dark:text-gray-300">
                  {`${currentOrigin}${urlEndpoint}`}
                </span>
              </div>
            </div>
            <TooltipHorizon
              trigger={
                <button
                  type="button"
                  onClick={handleCopyEndpoint}
                  className="flex h-10 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-400 dark:hover:bg-navy-600 dark:hover:text-gray-300"
                >
                  {isEndpointCopied ? (
                    <CheckIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <CopyIcon className="h-4 w-4" />
                  )}
                </button>
              }
              content={
                <span>{t("agents.trigger.apiEndpointCopyTooltip")}</span>
              }
              placement="top"
              extra=""
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={handleResetChanges}
          disabled={!hasChanges || isSaving}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={handleSaveConfiguration}
          disabled={isSaveButtonDisabled}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-brand-300 disabled:opacity-70 dark:bg-brand-400 dark:hover:bg-brand-500 dark:disabled:bg-brand-300"
        >
          {isSaving
            ? t("common.saving")
            : t("agents.trigger.saveConfiguration")}
        </button>
      </div>
    </div>
  );
};

export default Trigger;
